import { useEffect, useRef } from "react";

// Ensure the Turnstile script is loaded exactly once for the whole app.
// We don't use react-turnstile / npm packages — Cloudflare explicitly asks
// callers to load their script directly (no CDN mirrors, no proxying).
function ensureTurnstileScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("cf-turnstile-script")) return;
  const s = document.createElement("script");
  s.id = "cf-turnstile-script";
  s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

/**
 * Cloudflare Turnstile widget. Calls `onToken(token)` when the challenge
 * succeeds; token is single-use and expires after 300s. Parent form must
 * gate submission on receiving a non-empty token.
 */
export function TurnstileWidget({ onToken, onExpire, onError, testId = "turnstile-widget" }) {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  useEffect(() => {
    if (!siteKey) return;
    ensureTurnstileScript();

    let mounted = true;
    const tryRender = () => {
      if (!mounted) return;
      const cf = window.turnstile;
      if (!cf || !hostRef.current) {
        setTimeout(tryRender, 100);
        return;
      }
      // Guard against React StrictMode double-mount rendering twice.
      if (widgetIdRef.current) return;
      try {
        widgetIdRef.current = cf.render(hostRef.current, {
          sitekey: siteKey,
          theme: "auto",
          size: "flexible",
          action: "register",
          callback: (token) => onToken?.(token),
          "expired-callback": () => onExpire?.(),
          "error-callback": (code) => onError?.(code),
        });
      } catch (e) {
        // Ignore — turnstile.render can throw if the container is empty
        // for a beat during hot-reload; the retry loop will re-try.
      }
    };
    tryRender();

    return () => {
      mounted = false;
      const cf = window.turnstile;
      if (cf && widgetIdRef.current) {
        try { cf.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, onExpire, onError]);

  if (!siteKey) {
    // Local dev without keys — render a note but don't block the form.
    return (
      <div className="text-xs text-[#8C7F65] font-mono" data-testid={`${testId}-missing-key`}>
        CAPTCHA disabled (no site key configured)
      </div>
    );
  }

  return <div ref={hostRef} data-testid={testId} />;
}
